/**
 * The redstone simulation of one world: neighbour updates, scheduled ticks and
 * the behaviour of every component (power rules: power.ts, block ids: defs.ts).
 *
 * Same shape as the other block sims (fluid.ts, fire.ts): the owner of the
 * world reports block changes with `blockChanged` and calls `tick` once per
 * game tick (20 Hz). Offline that is the client, online the server.
 *
 *  - A block change queues an update for every reactive block within two cells.
 *    Updates run in FIFO order; a component whose output should change asks
 *    for a scheduled tick (a redstone tick is two game ticks) and flips then.
 *  - Scheduled ticks run in (time, priority, insertion) order, so the same
 *    world always evolves the same way.
 *  - At most `budget` units of work run per game tick; what is left carries
 *    over to the next one (a big contraption slows down instead of stalling
 *    the server).
 *  - Nothing but block ids is saved. When a chunk loads, its owner re-reports
 *    its edited cells, and every component re-derives what it was waiting for.
 */
import {
  BLOCKS, DOOR_BOTTOM_IDS, DOOR_STATE, IRON_TOGGLE, IS_OPAQUE, IS_SOLID, NEEDS_SUPPORT, RAIL_FAMILIES, RAIL_KIND, RAIL_SHAPE,
  SHAPE_STATE, TNT_BLOCK_ID, TOGGLE,
} from '../../blocks';
import { WORLD_HEIGHT } from '../../constants';
import { K, RS_DIR, RS_FLAG, RS_KIND, RS_POW, RS_VAR, rsId, withFlag, withPow } from './defs';
import { BINDINGS, COMPARATOR_SOURCES, POWER_HANDLERS } from './index';
import {
  DIRS, HORIZ, IS_CONDUCTOR, diodeInput, eachDustLink, emitWeak, noteData, receivedPower, sidesOf, signalFrom, type BlockReader,
} from './power';

export interface EntityCount { players: number; mobs: number; items: number; carts: number }

export interface RedstoneHost {
  getBlock(x: number, y: number, z: number): number;
  canEdit(x: number, y: number, z: number): boolean;
  setBlock(x: number, y: number, z: number, id: number): void;
  /** 'note' (d = pitch | instrument << 8), 'click' (d = 1 on / 0 off), 'piston' (1 out / 0 in), 'door' (1 open), 'fizz'. */
  fx?(kind: string, x: number, y: number, z: number, d: number): void;
  /** Entities touching the box (pressure plates, tripwire, detector rails). */
  entities?(x0: number, y0: number, z0: number, x1: number, y1: number, z1: number): EntityCount;
  /** A piston moved what is in this cell by (dx, dy, dz). */
  pushEntities?(x: number, y: number, z: number, dx: number, dy: number, dz: number): void;
  /** Strength of the sun, 0 (night) .. 15 (noon). */
  daylight?(): number;
  /** Light the TNT block in this cell. */
  primeTnt?(x: number, y: number, z: number): void;
  /** A piston broke the block `id` that was in this cell: drop it. */
  popBlock?(x: number, y: number, z: number, id: number): void;
}

const OFF = 2097152, SPAN = 4194304;
const keyOf = (x: number, y: number, z: number): number => ((x + OFF) * SPAN + (z + OFF)) * 256 + y;
const keyY = (k: number): number => k % 256;
const keyZ = (k: number): number => (Math.floor(k / 256) % SPAN) - OFF;
const keyX = (k: number): number => Math.floor(k / 256 / SPAN) - OFF;

interface Tick { t: number; pri: number; seq: number; x: number; y: number; z: number }

/** What a non-component block does with power: 1 swings (door, trapdoor, gate), 2 TNT, 3 powered / activator rail, 4 detector rail. */
const REACT = new Uint8Array(4096);
/** Pistons cannot move these (and never break them). */
const IMMOVABLE = new Uint8Array(4096);
const IMMOVABLE_WORDS = /Obsidian|Bedrock|Chest|Furnace|Smoker|Barrel|Enchanting|Brewing|Spawner|Portal|Anchor|Beacon|Hopper|Dropper|Dispenser|Vault|Lectern|Reinforced|Gateway|Sign|Shulker|Jukebox|Conduit|Bed$|Shrieker|Catalyst|Command|Barrier|Grindstone/;
for (const b of BLOCKS) {
  if (!b) continue;
  const id = b.id, st = SHAPE_STATE[id];
  if (DOOR_STATE[id] || (st && (st.kind === 'gate' || st.kind === 'trapdoor'))) REACT[id] = 1;
  else if (id === TNT_BLOCK_ID) REACT[id] = 2;
  else if (RAIL_KIND[id] === 1 || RAIL_KIND[id] === 2 || RAIL_KIND[id] === 5 || RAIL_KIND[id] === 6) REACT[id] = 3;
  else if (RAIL_KIND[id] === 3 || RAIL_KIND[id] === 4) REACT[id] = 4;
  if (IMMOVABLE_WORDS.test(b.name) || RS_KIND[id] === K.HEAD || (RS_KIND[id] === K.PISTON && RS_FLAG[id] === 1)) IMMOVABLE[id] = 1;
}
const isOpen = (id: number): boolean => DOOR_STATE[id]?.open ?? SHAPE_STATE[id]?.open ?? false;
/** Broken (and dropped) rather than pushed: everything a player walks through, and whatever needs support. */
const breaksWhenPushed = (id: number): boolean => id !== 0 && IMMOVABLE[id] === 0 && (IS_SOLID[id] === 0 || NEEDS_SUPPORT[id] === 1);

export const PUSH_LIMIT = 12;
/** Game ticks: a redstone tick is two. */
const RT = 2;
const TORCH_BURNOUT_COUNT = 8, TORCH_BURNOUT_WINDOW = 60, TORCH_BURNOUT_REST = 160;
const MAX_NETWORK = 4096;
const TRIPWIRE_REACH = 40;

export class RedstoneSim {
  /** Game ticks run so far. */
  now = 0;
  /** Units of work (updates, scheduled ticks, wires solved) per game tick. */
  budget = 4096;
  /** Work done in the last tick / updates still waiting after it (for tests and the debug overlay). */
  lastWork = 0;
  get backlog(): number { return this.queue.length - this.head + this.late.length; }

  private readonly w: BlockReader;
  private readonly queue: number[] = [];
  private head = 0;
  private readonly queued = new Set<number>();
  private readonly sched = new Map<number, Tick>();
  private readonly byTime = new Map<number, Tick[]>();
  private late: Tick[] = [];
  private seq = 0;
  /** Cells polled for entities / daylight: plates, tripwire, detector rails, daylight detectors. */
  private readonly watched = new Set<number>();
  private readonly lastOn = new Map<number, number>();
  /** Power last seen by doors, rails and onPowerChange handlers (edge detection; not saved). */
  private readonly seen = new Map<number, number>();
  private readonly torchFlips = new Map<number, number[]>();
  private readonly burned = new Set<number>();
  private readonly busy = new Map<number, number>();
  private inWire = false;

  constructor(private readonly host: RedstoneHost, world?: object) {
    this.w = host;
    BINDINGS.set(world ?? host, {
      powerAt: (x, y, z) => receivedPower(this.w, x, y, z),
      notifyNeighbors: (x, y, z) => { this.blockChanged(x, y, z); },
    });
    this.world = (world ?? host) as BlockReader;
  }
  private readonly world: BlockReader;

  // ---- queues ----------------------------------------------------------------

  private reactive(id: number): boolean {
    if (id === 0) return false;
    return RS_KIND[id] !== 0 || REACT[id] !== 0 || POWER_HANDLERS.has(id);
  }

  private enqueue(x: number, y: number, z: number): void {
    const k = keyOf(x, y, z);
    if (this.queued.has(k)) return;
    this.queued.add(k);
    this.queue.push(k);
  }

  /** The block at (x, y, z) changed (or what it holds did): everything reactive within two cells re-evaluates. */
  blockChanged(x: number, y: number, z: number): void {
    const w = this.w;
    for (let dy = -2; dy <= 2; dy++) {
      const cy = y + dy;
      if (cy < 0 || cy >= WORLD_HEIGHT) continue;
      const ry = 2 - Math.abs(dy);
      for (let dx = -ry; dx <= ry; dx++) {
        const rz = ry - Math.abs(dx);
        for (let dz = -rz; dz <= rz; dz++) {
          const id = w.getBlock(x + dx, cy, z + dz);
          if (id === 0 || !this.reactive(id)) continue;
          if (this.inWire && RS_KIND[id] === K.DUST) continue; // solved as one network
          this.enqueue(x + dx, cy, z + dz);
        }
      }
    }
    // observers watching this cell
    for (let d = 0; d < 6; d++) {
      const v = DIRS[d];
      const id = w.getBlock(x + v[0], y + v[1], z + v[2]);
      if (RS_KIND[id] === K.OBSERVER && RS_DIR[id] === (d ^ 1) && RS_POW[id] === 0) this.schedule(x + v[0], y + v[1], z + v[2], RT, 0);
    }
  }

  private schedule(x: number, y: number, z: number, delay: number, pri: number): boolean {
    const k = keyOf(x, y, z);
    if (this.sched.has(k)) return false;
    const t: Tick = { t: this.now + Math.max(1, delay), pri, seq: this.seq++, x, y, z };
    this.sched.set(k, t);
    const list = this.byTime.get(t.t);
    if (list) list.push(t); else this.byTime.set(t.t, [t]);
    return true;
  }

  private set(x: number, y: number, z: number, id: number): boolean {
    if (id < 0 || !this.host.canEdit(x, y, z)) return false;
    this.host.setBlock(x, y, z, id);
    return true;
  }

  // ---- the tick --------------------------------------------------------------

  tick(): void {
    this.now++;
    let left = this.budget;
    if (this.watched.size > 0) this.poll();

    const due = this.byTime.get(this.now);
    if (due) this.byTime.delete(this.now);
    let ticks = this.late;
    if (due) {
      due.sort((a, b) => a.pri - b.pri || a.seq - b.seq);
      ticks = ticks.length > 0 ? ticks.concat(due) : due;
    }
    this.late = [];
    for (let i = 0; i < ticks.length; i++) {
      const t = ticks[i];
      if (left <= 0) { this.late.push(t); continue; }
      this.sched.delete(keyOf(t.x, t.y, t.z));
      left--;
      if (this.host.canEdit(t.x, t.y, t.z)) this.runTick(t.x, t.y, t.z);
    }

    const q = this.queue;
    while (this.head < q.length && left > 0) {
      const k = q[this.head++];
      this.queued.delete(k);
      const x = keyX(k), y = keyY(k), z = keyZ(k);
      left -= this.host.canEdit(x, y, z) ? this.update(x, y, z) : 1;
    }
    if (this.head === q.length) { q.length = 0; this.head = 0; } else if (this.head > 8192) { q.splice(0, this.head); this.head = 0; }
    this.lastWork = this.budget - left;
  }

  // ---- neighbour updates -----------------------------------------------------

  /** Re-evaluate one cell; returns the work it cost. */
  private update(x: number, y: number, z: number): number {
    const w = this.w;
    const id = w.getBlock(x, y, z);
    const kind = RS_KIND[id];
    switch (kind) {
      case K.NONE: return this.updateConsumer(x, y, z, id);
      case K.DUST: {
        let want = receivedPower(w, x, y, z, false);
        if (want < 15) eachDustLink(w, x, y, z, (_x, _y, _z, n) => { if (RS_POW[n] - 1 > want) want = RS_POW[n] - 1; });
        return want === RS_POW[id] ? 1 : this.solveWires(x, y, z);
      }
      case K.TORCH: {
        const should = signalFrom(w, x, y, z, RS_DIR[id]) === 0;
        if (should !== (RS_POW[id] === 1)) this.schedule(x, y, z, RT, 0);
        return 1;
      }
      case K.BUTTON:
        if (RS_POW[id] === 1) this.schedule(x, y, z, RS_VAR[id] === 1 ? 30 : 20, 0);
        return 1;
      case K.PLATE: case K.TRIPWIRE: case K.DAYLIGHT:
        this.watched.add(keyOf(x, y, z));
        if (kind === K.TRIPWIRE) this.wakeHooks(x, y, z);
        return 1;
      case K.REPEATER: {
        const dir = RS_DIR[id];
        const lock = this.sideLocked(x, y, z, dir) ? 1 : 0;
        if (lock !== RS_FLAG[id]) { this.set(x, y, z, withFlag(id, lock)); return 1; }
        if (lock === 1) return 1;
        const should = diodeInput(w, x, y, z, dir) > 0;
        if (should !== (RS_POW[id] === 1)) this.schedule(x, y, z, (RS_VAR[id] + 1) * RT, this.facesDiode(x, y, z, dir) ? -3 : RS_POW[id] === 1 ? -2 : -1);
        return 1;
      }
      case K.COMPARATOR:
        if (this.comparatorOut(x, y, z, id) !== RS_POW[id]) this.schedule(x, y, z, RT, this.facesDiode(x, y, z, RS_DIR[id]) ? -3 : -1);
        return 1;
      case K.LAMP: {
        const on = receivedPower(w, x, y, z) > 0;
        if (on && RS_POW[id] === 0) this.set(x, y, z, withPow(id, 1));
        else if (!on && RS_POW[id] === 1) this.schedule(x, y, z, 2 * RT, 0);
        return 1;
      }
      case K.OBSERVER:
        if (RS_POW[id] === 1) this.schedule(x, y, z, RT, 0);
        return 1;
      case K.PISTON: {
        const dir = RS_DIR[id], v = DIRS[dir];
        const ext = RS_FLAG[id] === 1;
        if (ext) {
          const head = w.getBlock(x + v[0], y + v[1], z + v[2]);
          if (RS_KIND[head] !== K.HEAD || RS_DIR[head] !== dir) { this.set(x, y, z, rsId(K.PISTON, dir, 0, RS_VAR[id], 0)); return 1; }
        }
        if ((receivedPower(w, x, y, z, true, dir) > 0) !== ext) this.schedule(x, y, z, 1, 0);
        return 1;
      }
      case K.HEAD: {
        const dir = RS_DIR[id], v = DIRS[dir];
        const base = w.getBlock(x - v[0], y - v[1], z - v[2]);
        if (RS_KIND[base] !== K.PISTON || RS_DIR[base] !== dir || RS_FLAG[base] !== 1) this.set(x, y, z, 0);
        return 1;
      }
      case K.NOTE: {
        const on = receivedPower(w, x, y, z) > 0 ? 1 : 0;
        if (on !== RS_POW[id]) {
          this.set(x, y, z, withPow(id, on));
          if (on === 1 && w.getBlock(x, y + 1, z) === 0) this.host.fx?.('note', x, y, z, noteData(w, x, y, z, id));
        }
        return 1;
      }
      case K.TARGET:
        if (RS_POW[id] > 0) this.schedule(x, y, z, 4 * RT, 0);
        return 1;
      case K.HOOK: this.updateHook(x, y, z, id); return 2;
      default: return 1;
    }
  }

  private updateConsumer(x: number, y: number, z: number, id: number): number {
    const w = this.w, k = keyOf(x, y, z);
    const handler = POWER_HANDLERS.get(id);
    const react = REACT[id];
    if (react === 0 && !handler) return 1;
    let power = receivedPower(w, x, y, z);
    if (react === 1) {
      // doors, trapdoors, gates: a change of power sets them open / closed (both halves of a door count)
      const door = DOOR_STATE[id] !== undefined;
      const oy = DOOR_BOTTOM_IDS.has(id) ? y + 1 : y - 1;
      if (door && power === 0) power = receivedPower(w, x, oy, z);
      const on = power > 0;
      if (on !== this.seen.has(k)) {
        if (on) this.seen.set(k, 1); else this.seen.delete(k);
        if (door) { if (on) this.seen.set(keyOf(x, oy, z), 1); else this.seen.delete(keyOf(x, oy, z)); }
        if (isOpen(id) !== on) {
          const other = door ? w.getBlock(x, oy, z) : 0;
          this.set(x, y, z, TOGGLE[id] ?? IRON_TOGGLE[id] ?? -1);
          if (door && DOOR_STATE[other]) this.set(x, oy, z, TOGGLE[other] ?? IRON_TOGGLE[other] ?? -1);
          this.host.fx?.('door', x, y, z, on ? 1 : 0);
        }
      }
    } else if (react === 2) {
      if (power > 0) this.host.primeTnt?.(x, y, z);
    } else if (react === 3) {
      const direct = power > 0 ? 1 : 0;
      const kind = RAIL_KIND[id], offKind = kind <= 2 ? 1 : 5;
      const on = direct === 1 || this.railChainPowered(x, y, z, id);
      const want = RAIL_FAMILIES[on ? offKind + 1 : offKind][RAIL_SHAPE[id]];
      const changed = want !== id || direct !== (this.seen.get(k) ?? 0);
      if (direct) this.seen.set(k, 1); else this.seen.delete(k);
      if (want !== id) this.set(x, y, z, want);
      if (changed) this.walkRails(x, y, z, id, (rx, ry, rz) => { this.enqueue(rx, ry, rz); return false; });
      return 4;
    } else if (react === 4) {
      this.watched.add(k);
    }
    if (handler) {
      const p = receivedPower(w, x, y, z);
      if (p !== (this.seen.get(k) ?? 0)) {
        if (p > 0) this.seen.set(k, p); else this.seen.delete(k);
        handler(this.world, x, y, z, p);
      }
    }
    return 1;
  }

  // ---- dust ------------------------------------------------------------------

  /** Solve the whole wire network containing (x, y, z) at once and write each wire that changed, once. */
  private solveWires(x: number, y: number, z: number): number {
    const w = this.w;
    const keys: number[] = [keyOf(x, y, z)];
    const index = new Map<number, number>([[keys[0], 0]]);
    const adj: number[][] = [];
    const val: number[] = [];
    let truncated = false;
    for (let i = 0; i < keys.length; i++) {
      const cx = keyX(keys[i]), cy = keyY(keys[i]), cz = keyZ(keys[i]);
      let src = receivedPower(w, cx, cy, cz, false);
      const links: number[] = [];
      eachDustLink(w, cx, cy, cz, (lx, ly, lz, lid) => {
        const lk = keyOf(lx, ly, lz);
        let j = index.get(lk);
        if (j === undefined) {
          if (keys.length >= MAX_NETWORK || !this.host.canEdit(lx, ly, lz)) {
            // beyond the part solved now: its present value feeds in like any other source
            truncated = true;
            if (RS_POW[lid] - 1 > src) src = RS_POW[lid] - 1;
            return;
          }
          j = keys.length;
          keys.push(lk);
          index.set(lk, j);
        }
        links.push(j);
      });
      adj.push(links);
      val.push(src);
    }
    // flood from the strongest sources down
    const buckets: number[][] = [];
    for (let p = 0; p <= 15; p++) buckets.push([]);
    for (let i = 0; i < val.length; i++) buckets[val[i]].push(i);
    for (let p = 15; p >= 2; p--) {
      const b = buckets[p];
      for (let n = 0; n < b.length; n++) {
        const i = b[n];
        if (val[i] !== p) continue;
        for (const j of adj[i]) if (val[j] < p - 1) { val[j] = p - 1; buckets[p - 1].push(j); }
      }
    }
    this.inWire = !truncated;
    try {
      for (let i = 0; i < keys.length; i++) {
        const cx = keyX(keys[i]), cy = keyY(keys[i]), cz = keyZ(keys[i]);
        const id = w.getBlock(cx, cy, cz);
        if (RS_KIND[id] === K.DUST && RS_POW[id] !== val[i]) this.set(cx, cy, cz, withPow(id, val[i]));
      }
    } finally {
      this.inWire = false;
    }
    return keys.length;
  }

  // ---- diodes ----------------------------------------------------------------

  /** A powered repeater / comparator pointing into the side of the repeater at (x, y, z) locks it. */
  private sideLocked(x: number, y: number, z: number, dir: number): boolean {
    for (const s of sidesOf(dir)) {
      const v = DIRS[s];
      const id = this.w.getBlock(x + v[0], y, z + v[2]);
      const k = RS_KIND[id];
      if ((k === K.REPEATER || k === K.COMPARATOR) && RS_DIR[id] === (s ^ 1) && RS_POW[id] > 0) return true;
    }
    return false;
  }

  /** Does the diode point into another diode that does not point back (vanilla's tick priority rule)? */
  private facesDiode(x: number, y: number, z: number, dir: number): boolean {
    const v = DIRS[dir];
    const id = this.w.getBlock(x + v[0], y, z + v[2]);
    const k = RS_KIND[id];
    return (k === K.REPEATER || k === K.COMPARATOR) && RS_DIR[id] !== (dir ^ 1);
  }

  private comparatorOut(x: number, y: number, z: number, id: number): number {
    const w = this.w, dir = RS_DIR[id], b = DIRS[dir ^ 1];
    let input = diodeInput(w, x, y, z, dir);
    const bx = x + b[0], bz = z + b[2];
    const behind = w.getBlock(bx, y, bz);
    const direct = COMPARATOR_SOURCES.get(behind);
    if (direct) input = direct(this.world, bx, y, bz);
    else if (input < 15 && IS_CONDUCTOR[behind] === 1) {
      const far = COMPARATOR_SOURCES.get(w.getBlock(bx + b[0], y, bz + b[2]));
      if (far) input = Math.max(input, far(this.world, bx + b[0], y, bz + b[2]));
    }
    input = Math.max(0, Math.min(15, input | 0));
    let side = 0;
    for (const s of sidesOf(dir)) {
      const v = DIRS[s];
      const sid = w.getBlock(x + v[0], y, z + v[2]);
      const k = RS_KIND[sid];
      const p = k === K.DUST ? RS_POW[sid]
        : k === K.REPEATER || k === K.COMPARATOR || k === K.OBSERVER || (k === K.NONE && sid !== 0) ? emitWeak(w, x + v[0], y, z + v[2], sid, s ^ 1) : 0;
      if (p > side) side = p;
    }
    return RS_VAR[id] === 1 ? Math.max(0, input - side) : input >= side ? input : 0;
  }

  // ---- scheduled ticks -------------------------------------------------------

  private runTick(x: number, y: number, z: number): void {
    const w = this.w;
    const id = w.getBlock(x, y, z);
    switch (RS_KIND[id]) {
      case K.TORCH: {
        const k = keyOf(x, y, z);
        const lit = RS_POW[id] === 1;
        const should = signalFrom(w, x, y, z, RS_DIR[id]) === 0;
        if (this.burned.delete(k)) this.torchFlips.delete(k);
        if (lit && !should) {
          this.set(x, y, z, withPow(id, 0));
          const flips = (this.torchFlips.get(k) ?? []).filter((t) => t > this.now - TORCH_BURNOUT_WINDOW);
          flips.push(this.now);
          this.torchFlips.set(k, flips);
          if (flips.length >= TORCH_BURNOUT_COUNT) {
            // burnt out: stays dark until it has rested
            this.burned.add(k);
            this.host.fx?.('fizz', x, y, z, 0);
            this.sched.delete(k);
            this.schedule(x, y, z, TORCH_BURNOUT_REST, 0);
          }
        } else if (!lit && should && !this.burned.has(k)) this.set(x, y, z, withPow(id, 1));
        if (this.torchFlips.size > 4096) this.torchFlips.clear();
        break;
      }
      case K.BUTTON:
        if (RS_POW[id] === 1) { this.set(x, y, z, withPow(id, 0)); this.host.fx?.('click', x, y, z, 0); }
        break;
      case K.REPEATER: {
        if (RS_FLAG[id] === 1) break;
        const should = diodeInput(w, x, y, z, RS_DIR[id]) > 0;
        if (RS_POW[id] === 1) { if (!should) this.set(x, y, z, withPow(id, 0)); }
        else {
          this.set(x, y, z, withPow(id, 1));
          // a pulse shorter than the delay still comes out a full delay long
          if (!should) this.schedule(x, y, z, (RS_VAR[id] + 1) * RT, -2);
        }
        break;
      }
      case K.COMPARATOR: {
        const out = this.comparatorOut(x, y, z, id);
        if (out !== RS_POW[id]) this.set(x, y, z, withPow(id, out));
        break;
      }
      case K.LAMP:
        if (RS_POW[id] === 1 && receivedPower(w, x, y, z) === 0) this.set(x, y, z, withPow(id, 0));
        break;
      case K.OBSERVER:
        if (RS_POW[id] === 0) { this.set(x, y, z, withPow(id, 1)); this.schedule(x, y, z, RT, 0); }
        else this.set(x, y, z, withPow(id, 0));
        break;
      case K.TARGET:
        if (RS_POW[id] > 0) this.set(x, y, z, withPow(id, 0));
        break;
      case K.PISTON: this.movePiston(x, y, z, id); break;
      default: break;
    }
  }

  // ---- pistons ---------------------------------------------------------------

  private movePiston(x: number, y: number, z: number, id: number): void {
    const w = this.w, k = keyOf(x, y, z);
    const dir = RS_DIR[id], v = DIRS[dir], sticky = RS_VAR[id];
    const ext = RS_FLAG[id] === 1;
    const should = receivedPower(w, x, y, z, true, dir) > 0;
    if (should === ext) return;
    const until = this.busy.get(k) ?? 0;
    if (until > this.now) { this.schedule(x, y, z, until - this.now, 0); return; } // still moving
    if (this.busy.size > 4096) this.busy.clear();
    if (should) {
      // the column to push: up to PUSH_LIMIT blocks, ending in air or something that breaks
      const column: number[] = [];
      let cx = x + v[0], cy = y + v[1], cz = z + v[2];
      for (;;) {
        if (cy < 0 || cy >= WORLD_HEIGHT || !this.host.canEdit(cx, cy, cz)) return;
        const b = w.getBlock(cx, cy, cz);
        if (b === 0 || breaksWhenPushed(b)) { if (b !== 0) this.host.popBlock?.(cx, cy, cz, b); break; }
        if (IMMOVABLE[b] === 1 || column.length === PUSH_LIMIT) return;
        column.push(b);
        cx += v[0]; cy += v[1]; cz += v[2];
      }
      for (let i = column.length - 1; i >= 0; i--) {
        const px = x + v[0] * (i + 2), py = y + v[1] * (i + 2), pz = z + v[2] * (i + 2);
        this.host.pushEntities?.(px, py, pz, v[0], v[1], v[2]);
        this.set(px, py, pz, column[i]);
      }
      this.host.pushEntities?.(x + v[0], y + v[1], z + v[2], v[0], v[1], v[2]);
      this.set(x + v[0], y + v[1], z + v[2], rsId(K.HEAD, dir, 0, sticky, 0));
      this.set(x, y, z, rsId(K.PISTON, dir, 1, sticky, 1));
      this.host.fx?.('piston', x, y, z, 1);
    } else {
      this.set(x, y, z, rsId(K.PISTON, dir, 0, sticky, 0));
      const hx = x + v[0], hy = y + v[1], hz = z + v[2];
      const fx = hx + v[0], fy = hy + v[1], fz = hz + v[2];
      const far = sticky === 1 && fy >= 0 && fy < WORLD_HEIGHT && this.host.canEdit(fx, fy, fz) ? w.getBlock(fx, fy, fz) : 0;
      if (far !== 0 && IMMOVABLE[far] === 0 && !breaksWhenPushed(far)) {
        this.set(hx, hy, hz, far);
        this.set(fx, fy, fz, 0);
      } else this.set(hx, hy, hz, 0);
      this.host.fx?.('piston', x, y, z, 0);
    }
    this.busy.set(k, this.now + RT);
  }

  // ---- rails -----------------------------------------------------------------

  /** Visit the rails of the same family along the track, up to 8 each way; stop when `fn` returns true. */
  private walkRails(x: number, y: number, z: number, id: number, fn: (x: number, y: number, z: number, id: number) => boolean): boolean {
    const w = this.w;
    const fam = RAIL_KIND[id] <= 2 ? 1 : 5;
    const alongX = RAIL_SHAPE[id] === 1 || RAIL_SHAPE[id] === 2 || RAIL_SHAPE[id] === 3;
    for (const sign of [1, -1]) {
      let cx = x, cy = y, cz = z;
      for (let n = 0; n < 8; n++) {
        cx += alongX ? sign : 0; cz += alongX ? 0 : sign;
        let found = false;
        for (const dy of [0, 1, -1]) {
          const r = w.getBlock(cx, cy + dy, cz);
          const rk = RAIL_KIND[r];
          if (rk !== fam && rk !== fam + 1) continue;
          const rs = RAIL_SHAPE[r];
          if ((rs === 1 || rs === 2 || rs === 3) !== alongX) continue;
          cy += dy; found = true;
          if (fn(cx, cy, cz, r)) return true;
          break;
        }
        if (!found) break;
      }
    }
    return false;
  }
  private railChainPowered(x: number, y: number, z: number, id: number): boolean {
    return this.walkRails(x, y, z, id, (rx, ry, rz) => receivedPower(this.w, rx, ry, rz) > 0);
  }

  // ---- tripwire --------------------------------------------------------------

  /** A tripwire changed: the hooks at the ends of its lines re-check. */
  private wakeHooks(x: number, y: number, z: number): void {
    for (const d of HORIZ) {
      const v = DIRS[d];
      for (let n = 1; n <= TRIPWIRE_REACH; n++) {
        const id = this.w.getBlock(x + v[0] * n, y, z + v[2] * n);
        if (RS_KIND[id] === K.HOOK) { this.enqueue(x + v[0] * n, y, z + v[2] * n); break; }
        if (RS_KIND[id] !== K.TRIPWIRE) break;
      }
    }
  }

  private updateHook(x: number, y: number, z: number, id: number): void {
    const w = this.w, dir = RS_DIR[id], v = DIRS[dir ^ 1];
    let attached = 0, powered = 0, wires = 0;
    for (let n = 1; n <= TRIPWIRE_REACH; n++) {
      const o = w.getBlock(x + v[0] * n, y, z + v[2] * n);
      if (RS_KIND[o] === K.TRIPWIRE) { wires++; if (RS_POW[o] === 1) powered = 1; continue; }
      if (RS_KIND[o] === K.HOOK && RS_DIR[o] === (dir ^ 1) && wires > 0) attached = 1;
      break;
    }
    if (attached === 0) powered = 0;
    const want = rsId(K.HOOK, dir, powered, 0, attached);
    if (want !== id) {
      this.set(x, y, z, want);
      if (powered !== RS_POW[id]) this.host.fx?.('click', x, y, z, powered);
    }
  }

  // ---- entity- and sun-driven inputs ----------------------------------------

  private poll(): void {
    const w = this.w, host = this.host;
    for (const k of this.watched) {
      const x = keyX(k), y = keyY(k), z = keyZ(k);
      if (!host.canEdit(x, y, z)) { this.watched.delete(k); continue; }
      const id = w.getBlock(x, y, z);
      const kind = RS_KIND[id];
      if (kind === K.DAYLIGHT) {
        if ((this.now + k) % 20 !== 0 || !host.daylight) continue;
        let sun = host.daylight();
        for (let cy = y + 1; cy < WORLD_HEIGHT && sun > 0; cy++) if (IS_OPAQUE[w.getBlock(x, cy, z)] === 1) sun = 0;
        const want = RS_VAR[id] === 1 ? 15 - sun : sun;
        if (want !== RS_POW[id]) this.set(x, y, z, withPow(id, want));
        continue;
      }
      const rail = RAIL_KIND[id] === 3 || RAIL_KIND[id] === 4;
      if (kind !== K.PLATE && kind !== K.TRIPWIRE && !rail) { this.watched.delete(k); this.lastOn.delete(k); continue; }
      if (!host.entities) continue;
      const c = kind === K.TRIPWIRE ? host.entities(x, y + 1 / 16, z, x + 1, y + 3 / 16, z + 1) : host.entities(x + 1 / 8, y, z + 1 / 8, x + 7 / 8, y + 0.25, z + 7 / 8);
      const total = c.players + c.mobs + c.items + c.carts;
      let want: number, cur: number, hold = 20;
      if (rail) { want = c.carts > 0 ? 1 : 0; cur = RAIL_KIND[id] === 4 ? 1 : 0; }
      else if (kind === K.TRIPWIRE) { want = total > 0 ? 1 : 0; cur = RS_POW[id]; hold = 10; }
      else {
        cur = RS_POW[id];
        const variant = RS_VAR[id];
        want = variant === 0 ? (c.players + c.mobs > 0 ? 1 : 0) : variant === 1 ? (total > 0 ? 1 : 0)
          : variant === 2 ? Math.min(15, total) : Math.min(15, Math.ceil(total / 10));
        if (variant >= 2) hold = 10;
      }
      if (want > 0) this.lastOn.set(k, this.now);
      else if (cur > 0 && this.now - (this.lastOn.get(k) ?? -1000) < hold) continue; // stays down a moment
      if (want === cur) continue;
      if (want === 0) this.lastOn.delete(k);
      if (rail) this.set(x, y, z, RAIL_FAMILIES[want ? 4 : 3][RAIL_SHAPE[id]]);
      else this.set(x, y, z, withPow(id, want));
      if ((want > 0) !== (cur > 0)) host.fx?.('click', x, y, z, want > 0 ? 1 : 0);
    }
  }

  // ---- outside triggers ------------------------------------------------------

  /** A projectile hit the target block: it emits `power` for `ticks` game ticks. */
  targetHit(x: number, y: number, z: number, power: number, ticks = 8): void {
    const id = this.w.getBlock(x, y, z);
    if (RS_KIND[id] !== K.TARGET) return;
    this.sched.delete(keyOf(x, y, z));
    this.set(x, y, z, withPow(id, Math.max(1, Math.min(15, power | 0))));
    this.schedule(x, y, z, ticks, 0);
  }
}
